import React, { useState, useEffect, useRef } from 'react';
import { useFloating, offset, shift, flip, autoUpdate } from '@floating-ui/react';

interface DemoTourProps {
  isActive: boolean;
  onClose: () => void;
}

interface TourStep {
  id: string;
  title: string;
  content: string;
  target: string;
  placement: 'top' | 'bottom' | 'left' | 'right';
  action?: 'click' | 'scroll' | 'none';
}

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

// Cookie utility functions
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

const setCookie = (name: string, value: string, days: number = 365): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Career Pathways Portal!',
    content: 'Discover your perfect career path with our comprehensive job search and analysis tools. This guided tour will show you how to explore opportunities and find financial aid.',
    target: '.search-filters',
    placement: 'bottom',
    action: 'scroll'
  },
  {
    id: 'job-cards',
    title: 'Explore Job Opportunities',
    content: 'Each job card displays key information including salary, education requirements, and growth projections. Click on any card to see detailed information.',
    target: '[data-first="true"], .job-card:first-child',
    placement: 'right',
    action: 'scroll'
  },
  {
    id: 'job-modal',
    title: 'Detailed Job Information',
    content: 'This comprehensive view shows everything you need to know about the job: required skills, knowledge areas, technology needs, and career progression.',
    target: '.modal,.job-detail-modal,[role="dialog"]',
    placement: 'top',
    action: 'scroll'
  },
  {
    id: 'job-postings-info',
    title: 'Discover Job Postings',
    content: 'Use the "Job Postings" button to see active openings for this occupation and learn which employers are hiring right now.',
    target: '.job-postings-button',
    placement: 'top',
    action: 'scroll'
  },
  {
    id: 'businesses-info',
    title: 'View Business Breakdown',
    content: 'Click the "View Businesses" button to see which local employers hire for this occupation and how their employment trends are changing.',
    target: '.businesses-button',
    placement: 'top',
    action: 'scroll'
  },
  {
    id: 'all-industries-info',
    title: 'All Industries Overview',
    content: 'Use the "View Industries" button to explore every industry that employs this occupation, helping you understand the broader opportunities.',
    target: '.industries-button',
    placement: 'top',
    action: 'scroll'
  },
  {
    id: 'industries-modal',
    title: 'Industry Employment Data',
    content: 'Review how each industry supports this occupation, including employment levels, growth projections, and wage insights.',
    target: '.modal, .industries-modal, [role="dialog"]',
    placement: 'right',
    action: 'scroll'
  },
  {
    id: 'financial-aid-info',
    title: 'Financial Aid Resources',
    content: 'The Financial Aid section provides access to scholarships, grants, and financial assistance programs to help fund your education.',
    target: '.header-financial-aid-button',
    placement: 'bottom',
    action: 'scroll'
  },
  {
    id: 'financial-aid-click',
    title: 'Access Financial Aid',
    content: 'Click the Financial Aid button to explore available scholarships, grants, and financial assistance programs.',
    target: '.header-financial-aid-button',
    placement: 'bottom',
    action: 'scroll'
  },
  {
    id: 'financial-aid-modal',
    title: 'Financial Aid Programs',
    content: 'Browse comprehensive financial aid options including federal programs, scholarships, and grants to support your educational journey.',
    target: '.modal, .financial-aid-modal, [role="dialog"]',
    placement: 'right',
    action: 'scroll'
  },
  {
    id: 'complete',
    title: 'You\'re Ready to Explore!',
    content: 'You now have all the tools to explore careers, analyze job data, and find financial aid. Start searching for your dream career!',
    target: '.search-filters',
    placement: 'bottom',
    action: 'scroll'
  }
];

export const DemoTour: React.FC<DemoTourProps> = ({ isActive, onClose }) => {
const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);
  const [highlightRect, setHighlightRect] = useState<HighlightRect | null>(null);
  const [showNextButton, setShowNextButton] = useState(true);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Reset tour to first step whenever it is (re)started
  useEffect(() => {
    if (isActive) {
      setCurrentStep(0);
      setShowNextButton(true);
    }
  }, [isActive]);

  useEffect(() => {
    const handleRestart = () => {
      setCurrentStep(0);
      setShowNextButton(true);
    };

    window.addEventListener('startDemoTour', handleRestart);
    return () => window.removeEventListener('startDemoTour', handleRestart);
  }, []);

  // Check if user is first-time visitor
  useEffect(() => {
    const hasSeenTour = getCookie('demoTourCompleted');
    if (!hasSeenTour && !isActive) {
      // Auto-start tour for first-time users
      setTimeout(() => {
        // Trigger tour start via custom event
        window.dispatchEvent(new CustomEvent('startDemoTour'));
      }, 1000); // Small delay to ensure page is loaded
    }
  }, []);

  // Override placement for specific steps
  const getPlacement = () => {
    // For mobile devices, always use bottom placement
    if (isMobile()) {
      return 'bottom';
    }
    
    if (tourSteps[currentStep]?.id === 'industries-modal') {
      return 'right';
    }
    if (tourSteps[currentStep]?.id === 'financial-aid-modal') {
      return 'right';
    }
    return tourSteps[currentStep]?.placement || 'bottom';
  };

  // Check if mobile device
  const isMobile = () => {
    return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const { refs, floatingStyles, update } = useFloating({
    placement: getPlacement(),
    middleware: [
      offset(10), 
      shift({ padding: 8 }), 
      flip({ fallbackAxisSideDirection: 'end' })
    ],
    whileElementsMounted: autoUpdate,
  });

  // Force update floating UI when placement changes
  useEffect(() => {
    if (isActive && update) {
      update();
    }
  }, [currentStep, isActive, update]);

  // Handle scrolling when tooltip goes off-screen
  useEffect(() => {
    if (isActive && tooltipRef.current) {
      const tooltip = tooltipRef.current;
      const rect = tooltip.getBoundingClientRect();
      
      // Check if tooltip is off-screen horizontally
      if (rect.left < 0 || rect.right > window.innerWidth) {
        // Scroll the highlighted element into view
        if (highlightedElement) {
          highlightedElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
        }
      }
    }
  }, [currentStep, isActive, highlightedElement]);

  // Ensure mobile tooltip positioning
  useEffect(() => {
    if (isActive && isMobile() && tooltipRef.current) {
      const step = tourSteps[currentStep];
      const isModalStep = step.id.includes('modal');
      
      // Check if there's a modal present
      const modalElement = document.querySelector('.modal, [role="dialog"]');
      const hasModal = !!modalElement;
      
      // Force mobile positioning
      Object.assign(tooltipRef.current.style, {
        position: 'fixed',
        left: '50%',
        top: isModalStep || hasModal ? 'auto' : 'auto',
        bottom: isModalStep || hasModal ? '10px' : '20px',
        transform: 'translateX(-50%)',
        zIndex: 999999,
        maxWidth: '90%',
        fontSize: '14px',
        width: '90%',
        margin: '0 auto',
        pointerEvents: 'auto',
      });
    }
  }, [currentStep, isActive]);

  useEffect(() => {
    if (!isActive) {
      setHighlightRect(null);
      return;
    }

    const step = tourSteps[currentStep];
    let targetElement = document.querySelector(step.target) as HTMLElement;

    const findElement = (selector: string, onFound: (el: HTMLElement) => void) => {
      const el = document.querySelector(selector) as HTMLElement;
      if (el) {
        onFound(el);
      } else {
        setTimeout(() => findElement(selector, onFound), 500);
      }
    };

    if (step.id === 'job-modal' && !targetElement) {
      findElement('.job-detail-modal, [data-testid="job-modal"], .modal, [role="dialog"]', () => setCurrentStep(currentStep));
      return;
    }
    if (step.id === 'industries-modal' && !targetElement) {
      findElement('.industries-modal, [data-testid="industries-modal"], .modal, [role="dialog"]', () => setCurrentStep(currentStep));
      return;
    }
    if (step.id === 'financial-aid-modal' && !targetElement) {
      findElement('.financial-aid-modal, [data-testid="financial-aid-modal"], .modal, [role="dialog"]', () => setCurrentStep(currentStep));
      return;
    }
    // Wait for job cards to load if target element is not found
    if (step.id === 'job-cards' && !targetElement) {
      findElement('[data-first="true"], .job-card:first-child', () => {
        setCurrentStep(currentStep);
      });
      return;
    }
    

    if (targetElement) {
      if (highlightedElement) {
        highlightedElement.style.position = '';
        highlightedElement.style.zIndex = '';
      }

      const rect = targetElement.getBoundingClientRect();
      setHighlightRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });

      targetElement.style.position = 'relative';
      targetElement.style.zIndex = '10001';
      setHighlightedElement(targetElement);

      refs.setReference(targetElement);
      if (tooltipRef.current) {
        refs.setFloating(tooltipRef.current);
      }

      // Only scroll for non-job-card steps to prevent positioning issues
      if (step.id !== 'job-cards') {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      const handleModalPositioning = (modalType: 'industries' | 'financial-aid') => {
        const modalElement = document.querySelector(`.modal, .${modalType}-modal, [role="dialog"]`) as HTMLElement;
        if (modalElement && tooltipRef.current) {
          refs.setReference(modalElement);
          refs.setFloating(tooltipRef.current);
          
          if (isMobile()) {
            // For mobile, position tooltip at bottom center
            Object.assign(tooltipRef.current.style, {
              position: 'fixed',
              left: '50%',
              top: 'auto',
              bottom: '20px',
              transform: 'translateX(-50%)',
              zIndex: '10003',
              maxWidth: '90%',
              fontSize: '14px',
            });
          } else {
            // For desktop, use floating UI positioning
            Object.assign(tooltipRef.current.style, {
              position: 'fixed',
              left: '50%',
              top: 'auto',
              bottom: '20px',
              transform: 'translateX(-50%)',
              zIndex: '10003',
              maxWidth: 'auto',
              fontSize: 'auto',
            });
          }
        }
      };

      if (step.id === 'industries-modal') handleModalPositioning('industries');
      if (step.id === 'financial-aid-modal') handleModalPositioning('financial-aid');

      if (step.id === 'financial-aid-info') {
        setTimeout(() => window.dispatchEvent(new CustomEvent('tourClickCloseIndustries')), 500);
      }
      
        setShowNextButton(true);
      if (step.id === 'financial-aid-click') {
        setShowNextButton(false);
        setTimeout(() => setShowNextButton(true), 1000);
      }
    }

    return () => {
      if (highlightedElement) {
        highlightedElement.style.zIndex = '';
        highlightedElement.style.position = '';
      }
    };
  }, [currentStep, isActive, refs]);

  useEffect(() => {
    if (!isActive) return;
    const event = isActive ? 'tourStarted' : 'tourEnded';
    window.dispatchEvent(new CustomEvent(event));
  }, [isActive]);

  const nextStep = () => {
    const clickElement = (selector: string, eventName: string) => {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        element.click();
        setTimeout(() => window.dispatchEvent(new CustomEvent(eventName)), 100);
      }
    };

    const currentStepId = tourSteps[currentStep]?.id;

    if (currentStepId === 'job-cards') {
      clickElement('[data-first="true"]', 'tourClickJobCard');
    }

    if (currentStepId === 'all-industries-info') {
      clickElement('.industries-button', 'tourClickIndustries');
    }

    if (currentStepId === 'financial-aid-click') {
      clickElement('.header-financial-aid-button', 'tourClickHeaderFinancialAid');
    }

    if (currentStepId === 'industries-modal') {
      setTimeout(() => window.dispatchEvent(new CustomEvent('tourClickCloseIndustries')), 150);
      setTimeout(() => window.dispatchEvent(new CustomEvent('tourCloseJobDetail')), 220);
      setTimeout(() => setCurrentStep((prev) => Math.min(prev + 1, tourSteps.length - 1)), 280);
      return;
    }

    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const completeTour = () => {
    if (highlightedElement) {
      highlightedElement.style.zIndex = '';
      highlightedElement.style.position = '';
    }
    setHighlightRect(null);
    window.dispatchEvent(new CustomEvent('closeFinancialAidModal'));
    setCookie('demoTourCompleted', 'true', 365);
    // Dispatch event to notify that demo tour is completed
    window.dispatchEvent(new CustomEvent('demoTourCompleted'));
    onClose();
  };

  if (!isActive) return null;

  const step = tourSteps[currentStep];

  return (
    <div className="fixed inset-0 z-[999999]">
      {highlightRect && !step.id.includes('modal') && (
        <>
          {/* Top overlay */}
          <div className="fixed bg-black bg-opacity-70 backdrop-blur-sm" style={{ top: 0, left: 0, width: '100%', height: `${highlightRect.top}px`, zIndex: 10000 }}></div>
          {/* Bottom overlay */}
          <div className="fixed bg-black bg-opacity-70 backdrop-blur-sm" style={{ top: `${highlightRect.top + highlightRect.height}px`, left: 0, width: '100%', bottom: 0, zIndex: 10000 }}></div>
          {/* Left overlay */}
          <div className="fixed bg-black bg-opacity-70 backdrop-blur-sm" style={{ top: `${highlightRect.top}px`, left: 0, width: `${highlightRect.left}px`, height: `${highlightRect.height}px`, zIndex: 10000 }}></div>
          {/* Right overlay */}
          <div className="fixed bg-black bg-opacity-70 backdrop-blur-sm" style={{ top: `${highlightRect.top}px`, left: `${highlightRect.left + highlightRect.width}px`, right: 0, height: `${highlightRect.height}px`, zIndex: 10000 }}></div>
        </>
      )}

      {/* Professional tooltip */}
      <div 
        ref={tooltipRef}
        className="absolute bg-white rounded-xl shadow-2xl border border-gray-200 max-w-sm"
        style={isMobile() ? {
          position: 'fixed',
          left: '50%',
          top: 'auto',
          bottom: (step.id.includes('modal') || document.querySelector('.modal, [role="dialog"]')) ? '10px' : '20px',
          transform: 'translateX(-50%)',
          zIndex: 999999,
          maxWidth: '90%',
          fontSize: '14px',
          width: '90%',
          margin: '0 auto',
          pointerEvents: 'auto',
        } : {
          ...floatingStyles,
          zIndex: 999999,
          // Add scroll behavior for desktop
          scrollBehavior: 'smooth'
        }}
      >
        <button onClick={completeTour} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        <div className="flex justify-between items-center mb-3 px-4 pt-4">
          <span className="text-xs text-gray-500 font-medium">Step {currentStep + 1} of {tourSteps.length}</span>
          <div className="flex space-x-1">
            {tourSteps.map((_, index) => <div key={index} className={`w-1.5 h-1.5 rounded-full ${index === currentStep ? 'bg-blue-500' : 'bg-gray-300'}`} />)}
          </div>
        </div>

        <div className="px-4 pb-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">{step.title}</h3>
          <p className="text-sm text-gray-600 mb-4 leading-relaxed">{step.content}</p>
        <div className="flex justify-between items-center">
            <button onClick={prevStep} disabled={currentStep === 0} className={`px-3 py-1.5 rounded-md text-xs font-medium ${currentStep === 0 ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Previous</button>
          <div className="flex space-x-2">
              <button onClick={completeTour} className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 font-medium">Skip Tour</button>
              {showNextButton && <button onClick={nextStep} className="px-4 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs font-medium">{currentStep === tourSteps.length - 1 ? 'Finish' : 'Next'}</button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 
